/**
 * Central configuration for RailTwin. Endpoint URLs live in config.json at
 * the project root; everything else (track geometry constants, LOD radii,
 * camera home) lives here so the rest of the app imports a single source of
 * truth. The only env var retained is VITE_CESIUM_ION_TOKEN (a secret).
 */

import endpoints from '../../config.json';

export const config = {
  ion: {
    /** Optional — only used for global fallback terrain. Empty = disabled. */
    token: (import.meta.env.VITE_CESIUM_ION_TOKEN as string | undefined) ?? '',
  },

  imagery: {
    /** RESTful WMTS tile-URL template (resolved against GetCapabilities, S1). */
    wmtsTileTemplate: endpoints.imagery.wmtsTileTemplate,
    imageServer: endpoints.imagery.imageServer,
    /** WMTS layer identifier as published by the service (note Basemaps_ prefix). */
    layer: 'Basemaps_LatestStateProgram_AllUsers',
    style: 'default',
    /** OGC well-known set that matches Cesium's WebMercatorTilingScheme exactly. */
    tileMatrixSetID: 'GoogleMapsCompatible',
    format: 'image/png',
  },

  terrain: {
    demImageServer: endpoints.terrain.demImageServer,
    /** Self-hosted quantized-mesh tiles (path B). Empty = try path A / Ion. */
    tilesUrl: endpoints.terrain.tilesUrl,
  },

  rail: {
    tilesUrl: endpoints.rail.tilesUrl,
  },

  /**
   * Physical track parameters (all metres). Gauge defaults to Queensland narrow
   * gauge; per-feature GAUGE attribute overrides via gaugeForFeature().
   */
  track: {
    railHeightAboveGround: 0.7,
    defaultGauge: 1.067,
    standardGauge: 1.435,
    sleeperLength: 2.15,
    sleeperPitch: 0.685,
    sleeperWidth: 0.25,
    sleeperDepth: 0.15,
    ballastTopWidth: 2.6,
    ballastBottomWidth: 3.8,
    ballastHeight: 0.3,
    railProfileHeight: 0.07,
    railProfileWidth: 0.065,
    /**
     * OHLE dimensions follow a Queensland Rail 25 kV cantilever cross-section
     * drawing supplied by the project owner (sheet B/30/360/U):
     *  - mast 7.0 m tall (drawing: 7100 mm), offset 3.0 m from track centreline
     *  - horizontal cantilever arm at 5850 mm above rail level, carrying the
     *    top (catenary) wire at its outer end above the centreline
     *  - a second arm rising from 4500 mm on the mast to the cantilever tip
     *  - wires are tensioned, so each span is a STRAIGHT line between masts
     *    (no sag modelled)
     * Assumptions not on the drawing: the contact wire runs at 5.0 m above
     * rail level (between the 4.5 m arm root and the 5.85 m top wire);
     * droppers between the wires are not modelled.
     */
    ohle: {
      contactWireHeightAboveRail: 5.0,
      topWireHeightAboveRail: 5.85,
      mastHeight: 7.0,
      mastOffset: 3.0,
      cantileverArmHeight: 5.85,
      risingArmRootHeight: 4.5,
      mastSpacing: 55,
    },
  },

  /** Distance-based LOD band radii (metres of ground distance from camera). */
  lod: {
    nearBuild: 800,
    nearDispose: 1000,
    midBuild: 4000,
    midDispose: 5000,
    maxNearTiles: 24,
    maxMidTiles: 96,
    tilesPerFrame: 2,
  },

  /** Camera home — Greater Brisbane / SEQ (the first-milestone scope). */
  home: {
    lon: 153.025,
    lat: -27.47,
    height: 6000,
  },
} as const;

/** Resolve track gauge (m) from a rail feature's GAUGE attribute. */
export function gaugeForFeature(gauge: string | undefined | null): number {
  if (gauge && /standard/i.test(gauge) && !/narrow/i.test(gauge)) {
    return config.track.standardGauge;
  }
  return config.track.defaultGauge;
}
